<script lang="ts">
	import type { BatchHistoryEntry, BatchItem } from '#lib/batch/types';
	import { toBatchRow } from '#lib/batch/types';
	import { batchToCsv, batchToJson } from '#lib/batch/export';
	import { downloadFile } from '#lib/report/export';
	import { t } from '#lib/i18n/state.svelte';
	import { scoreTone } from '#lib/ui/labels';

	interface Props {
		items: BatchItem[];
		entry: BatchHistoryEntry | null;
		previous: BatchHistoryEntry | null;
		ondetail: (index: number) => void;
		onnew: () => void;
	}
	let { items, entry, previous, ondetail, onnew }: Props = $props();

	type Sort = 'desc' | 'asc';
	let sort = $state<Sort>('desc');
	let grade = $state<'all' | 'A' | 'B' | 'C' | 'D'>('all');

	const results = $derived(
		items
			.map((item, index) => ({ item, index, row: toBatchRow(item) }))
			.filter((r): r is { item: BatchItem; index: number; row: NonNullable<ReturnType<typeof toBatchRow>> } => r.row !== null)
	);

	const rows = $derived(
		results
			.filter((r) => grade === 'all' || r.row.grade === grade)
			.sort((a, b) => (sort === 'desc' ? b.row.score - a.row.score : a.row.score - b.row.score))
	);

	const failed = $derived(items.filter((i) => i.status === 'error').length);
	const cancelled = $derived(items.filter((i) => i.status === 'cancelled').length);

	function delta(fileName: string, score: number): number | null {
		const old = previous?.files.find((f) => f.fileName === fileName);
		return old ? score - old.score : null;
	}

	function exportCsv() {
		downloadFile(batchToCsv(results.map((r) => r.row)), 'repcheck-batch.csv', 'text/csv');
	}

	function exportJson() {
		downloadFile(
			batchToJson(results.map((r) => r.row), { id: entry?.id, date: entry?.date }),
			'repcheck-batch.json',
			'application/json'
		);
	}
</script>

<section class="batch-summary">
	<div class="head">
		<div>
			<p class="eyebrow">{t('batch.eyebrow')}</p>
			<h1>{t('batch.summaryTitle')}</h1>
			<p class="lede">
				{t('batch.summarySub', { done: results.length, total: items.length })}
				{#if entry}<span class="mono"> · {entry.id}</span>{/if}
			</p>
		</div>
		<div class="head-actions">
			<button class="btn btn-sm" onclick={exportCsv}>{t('batch.csv')}</button>
			<button class="btn btn-sm" onclick={exportJson}>{t('batch.json')}</button>
			<button class="btn btn-sm" onclick={onnew}>{t('batch.new')}</button>
		</div>
	</div>

	{#if previous}
		<p class="previous">{t('batch.previous', { id: previous.id })}</p>
	{/if}
	{#if failed || cancelled}
		<p class="note">{t('batch.partial', { failed, cancelled })}</p>
	{/if}

	<div class="filters">
		<div class="seg" role="group" aria-label={t('batch.sort')}>
			<button class="seg-btn" class:active={sort === 'desc'} onclick={() => (sort = 'desc')}>
				{t('batch.sortDesc')}
			</button>
			<button class="seg-btn" class:active={sort === 'asc'} onclick={() => (sort = 'asc')}>
				{t('batch.sortAsc')}
			</button>
		</div>
		<label class="filter">
			<span>{t('batch.filterGrade')}</span>
			<select bind:value={grade}>
				<option value="all">{t('batch.allGrades')}</option>
				{#each ['A', 'B', 'C', 'D'] as g (g)}
					<option value={g}>{g}</option>
				{/each}
			</select>
		</label>
	</div>

	<div class="table-wrap">
		<table>
			<thead>
				<tr>
					<th>#</th>
					<th>{t('batch.file')}</th>
					<th>{t('batch.score')}</th>
					<th>{t('batch.grade')}</th>
					<th>{t('severity.critical')}</th>
					<th>{t('severity.warning')}</th>
					<th>{t('severity.info')}</th>
					{#if previous}<th>{t('batch.change')}</th>{/if}
					<th></th>
				</tr>
			</thead>
			<tbody>
				{#each rows as r, i (r.item.id)}
					<tr>
						<td class="mono">{i + 1}</td>
						<td class="fname">{r.row.fileName}</td>
						<td class="num" style:color={scoreTone(r.row.score)}>{r.row.score}</td>
						<td><span class="chip chip-neutral">{r.row.grade}</span></td>
						<td class="num">{r.row.critical}</td>
						<td class="num">{r.row.warning}</td>
						<td class="num">{r.row.info}</td>
						{#if previous}
							{@const d = delta(r.row.fileName, r.row.score)}
							<td class="num" class:up={(d ?? 0) > 0} class:down={(d ?? 0) < 0}>
								{d === null ? '—' : `${d > 0 ? '+' : ''}${d}`}
							</td>
						{/if}
						<td class="open">
							<button class="btn btn-ghost btn-sm" onclick={() => ondetail(r.index)}>
								{t('batch.open')}
							</button>
						</td>
					</tr>
				{/each}
			</tbody>
		</table>
	</div>

	{#if rows.length === 0}
		<p class="empty">{t('batch.empty')}</p>
	{/if}
</section>

<style>
	.batch-summary {
		border: 1px solid var(--line);
		border-radius: var(--radius);
		background: #fff;
		padding: clamp(1.2rem, 3vw, 2rem);
	}
	.head {
		display: flex;
		justify-content: space-between;
		gap: 1rem;
		flex-wrap: wrap;
	}
	.eyebrow {
		margin: 0 0 0.3rem;
		font-size: 0.7rem;
		letter-spacing: 0.1em;
		text-transform: uppercase;
		color: var(--muted);
		font-weight: 650;
	}
	h1 {
		font-size: 1.4rem;
		margin: 0 0 0.3rem;
	}
	.lede {
		margin: 0;
		color: var(--ink-2);
		font-size: 0.86rem;
	}
	.head-actions {
		display: flex;
		gap: 0.35rem;
		flex-wrap: wrap;
		align-items: flex-start;
	}
	.previous,
	.note {
		margin: 0.7rem 0 0;
		font-size: 0.8rem;
		color: var(--accent-ink);
	}
	.note {
		color: var(--warning);
	}
	.filters {
		display: flex;
		gap: 0.8rem;
		align-items: center;
		flex-wrap: wrap;
		margin: 1rem 0 0.6rem;
	}
	.seg {
		display: inline-flex;
		border: 1px solid var(--line-strong);
		border-radius: 8px;
		overflow: hidden;
		background: #fff;
	}
	.seg-btn {
		border: 0;
		background: transparent;
		padding: 0.35rem 0.6rem;
		font-size: 0.78rem;
		cursor: pointer;
		color: var(--muted);
	}
	.seg-btn.active {
		background: var(--ink);
		color: var(--paper);
	}
	.filter {
		display: inline-flex;
		align-items: center;
		gap: 0.4rem;
		font-size: 0.8rem;
		color: var(--muted);
	}
	.filter select {
		padding: 0.3rem 0.4rem;
		border: 1px solid var(--line-strong);
		border-radius: 7px;
		background: #fff;
		font: inherit;
	}
	.table-wrap {
		overflow-x: auto;
	}
	table {
		width: 100%;
		border-collapse: collapse;
		font-size: 0.82rem;
		min-width: 40rem;
	}
	th,
	td {
		text-align: left;
		padding: 0.5rem 0.55rem;
		border-bottom: 1px solid var(--line);
		white-space: nowrap;
	}
	th {
		font-size: 0.68rem;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: var(--muted);
	}
	.fname {
		max-width: 18rem;
		overflow-wrap: anywhere;
		white-space: normal;
	}
	.num {
		text-align: right;
		font-variant-numeric: tabular-nums;
	}
	.up {
		color: var(--ok);
	}
	.down {
		color: var(--critical);
	}
	.open {
		text-align: right;
	}
	.empty {
		color: var(--muted);
		font-size: 0.85rem;
	}
	@media (max-width: 560px) {
		.head-actions {
			width: 100%;
		}
		.head-actions .btn {
			flex: 1 1 auto;
		}
	}
</style>
